import type { McpSetupResponse } from "@/mcp/types";
import { in_electron } from "@/utils/in";
import { optionalStringProperty, property } from "@/utils/unknown";

export type McpPluginStatusKind =
  | "disabled"
  | "checking"
  | "ready"
  | "pending"
  | "error";

export type McpPluginStatus = {
  kind: McpPluginStatusKind;
  label: string;
  message?: string;
};

export const checkingMcpPluginStatus: McpPluginStatus = {
  kind: "checking",
  label: "Checking",
};

export function parseMcpSetupResponse(value: unknown): McpSetupResponse | null {
  const enabled = property(value, "enabled");
  const controllerReady = property(value, "controllerReady");
  const listening = property(value, "listening");
  const endpoint = property(value, "endpoint");
  const version = property(value, "version");
  const token = optionalStringProperty(value, "token");
  const error = optionalStringProperty(value, "error");

  if (typeof enabled !== "boolean") return null;
  if (typeof controllerReady !== "boolean") return null;
  if (typeof listening !== "boolean") return null;
  if (typeof endpoint !== "string" || endpoint.length === 0) return null;
  if (typeof version !== "string" || version.length === 0) return null;
  if (token === null || error === null) return null;

  return {
    enabled,
    controllerReady,
    listening,
    endpoint,
    version,
    ...(token ? { token } : {}),
    ...(error ? { error } : {}),
  };
}

function desktopRequiredResponse(): McpSetupResponse {
  return {
    enabled: false,
    controllerReady: false,
    listening: false,
    endpoint: "http://127.0.0.1:8765/mcp",
    version: "unknown",
    error: "The MCP server is only available in Routine Desktop.",
  };
}

async function invokeMcpSetup(
  channel: "mcp-status" | "mcp-setup" | "mcp-set-enabled",
  enabled?: boolean
): Promise<McpSetupResponse> {
  if (!in_electron) return desktopRequiredResponse();

  const electron: typeof import("electron") = require("electron");
  const response: unknown = await electron.ipcRenderer.invoke(channel, enabled);
  const parsed = parseMcpSetupResponse(response);
  if (!parsed) throw new Error("Routine returned an invalid MCP setup status.");
  return parsed;
}

export function getMcpSetup(): Promise<McpSetupResponse> {
  return invokeMcpSetup("mcp-setup");
}

export function getMcpStatus(): Promise<McpSetupResponse> {
  return invokeMcpSetup("mcp-status");
}

export function setMcpEnabled(enabled: boolean): Promise<McpSetupResponse> {
  return invokeMcpSetup("mcp-set-enabled", enabled);
}

export function mcpPluginStatus(
  setup: McpSetupResponse,
  desktop = in_electron
): McpPluginStatus {
  if (!desktop) {
    return {
      kind: "error",
      label: "Desktop app required",
      message: "The MCP server is only available in Routine Desktop.",
    };
  }
  if (setup.error) {
    return {
      kind: "error",
      label: "Unavailable",
      message: setup.error,
    };
  }
  if (!setup.enabled) return { kind: "disabled", label: "Disabled" };
  if (!setup.controllerReady) {
    return {
      kind: "pending",
      label: "Waiting for login",
      message: "Keep Routine running and logged in to use MCP.",
    };
  }
  if (setup.listening) {
    return {
      kind: "ready",
      label: "Enabled",
      message: "Routine MCP is available to configured clients.",
    };
  }
  return {
    kind: "pending",
    label: "Starting",
    message: "Routine is starting the MCP endpoint.",
  };
}
